<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="enrollment-boost-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              In today's competitive educational landscape, standing out is more
              important than ever. Parents and students are looking for schools
              that offer not just quality instruction, but memorable experiences
              that spark curiosity and passion. By weaving unique learning
              experiences—like live drone-powered field trips—into your
              curriculum, you'll not only enrich classroom lessons, but also
              create a powerful draw for new families evaluating where to
              enroll.
            </p>
          </section>

          <section class="blog-section">
            <h2>Stand Out in a Crowded Market</h2>
            <p>
              Offering one-of-a-kind activities sends a clear message: your
              school values innovation and real-world relevance. When your
              open-house brochure highlights drone tours over active volcanoes
              or live explorations of coral reefs, prospective parents see that
              you're committed to immersive learning, not just rote
              memorization. This kind of programming often becomes the talking
              point at community events and local media, amplifying your reach
              without extra ad spend.
            </p>
          </section>

          <section class="blog-section">
            <h2>Turn Learning into an Adventure</h2>
            <p>
              Kids remember thrills—and they tell their friends. A geography
              lesson that literally takes students soaring above mountain ranges
              or skimming coastal cliffs creates "wow" moments that textbooks
              can't match. When families hear about your school's virtual
              journeys to remote locations, they picture engaged, excited
              learners—an image that resonates far more deeply than standard
              classroom photos.
            </p>
          </section>

          <section class="blog-section">
            <h2>Showcase Innovation in Action</h2>
            <p>
              Unique experiences also give you rich content for marketing. Share
              short clips of students directing a drone pilot, snapshots of
              their on-screen discoveries, and testimonial quotes about how
              these lessons make abstract concepts concrete. Posting these
              stories on your website and social channels does double duty: it
              keeps current families proud and draws the attention of prospects
              who want that same level of engagement for their own children.
            </p>
          </section>

          <section class="blog-section">
            <h2>Concrete Benefits for Enrollment</h2>
            <ul class="benefits-list">
              <li>
                <strong>Word-of-Mouth Referrals:</strong> Exciting programs get
                talked about—parents recommend schools that surprise and
                delight.
              </li>
              <li>
                <strong>Higher Open-House Turnout:</strong> Live demonstrations
                of Remote Outdoor Classes give visitors a genuine sample of your
                school's culture.
              </li>
              <li>
                <strong>Stronger Retention:</strong> Engaged students feel more
                connected to their school, making families less likely to look
                elsewhere.
              </li>
            </ul>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Conclusion</h2>
            <p>
              Boosting enrollment isn't just about tweaking your marketing
              line—it's about delivering the kind of learning that families
              can't find anywhere else. By integrating live, interactive
              experiences like Skyhost's drone tours, you give students—and
              prospective parents—a compelling reason to choose your school.
            </p>
            <p>
              Ready to set your school apart? Learn more at
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >
              and discover how easy it is to launch your first virtual field
              trip.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "EnrollmentBoostBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "enrollment-boost";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.enrollment-boost-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.benefits-list {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
}

.benefits-list li {
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
